import { useState, useEffect } from "react";
import axios from "axios";
import { Landmark, Users, Globe2, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ORIENT_COLORS = { care: "#10b981", utility: "#3b82f6", creative: "#a855f7", extraction: "#ef4444" };

export const InternationalBodies = () => {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/research/international-bodies`, { timeout: 20000 })
      .then(r => { if (r.data?.total > 0) setData(r.data); })
      .catch(() => {});
  }, []);

  if (!data) return null;
  const shown = expanded ? data.reports : data.reports.slice(0, 5);

  return (
    <div data-testid="international-bodies-spotlight" className="bg-blue-500/[0.04] border border-blue-500/15 rounded-xl p-5">
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <Landmark className="w-4 h-4 text-blue-400" />
        <p className="text-[10px] font-bold text-blue-400/80 uppercase tracking-[0.2em]">International Bodies — UN System Research</p>
      </div>
      <p className="text-[11px] text-slate-400 mb-4">
        Intergovernmental AI research authored by the most geographically and gender-diverse expert groups in the field — from the UN's 39-member advisory body (33 countries) to WHO, ILO, UNESCO and UNICEF expert panels.
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-3">
          <p className="text-lg font-black text-white">{data.total}</p>
          <p className="text-[9px] text-slate-500 uppercase tracking-wider">Landmark reports</p>
        </div>
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-3">
          <p className="text-lg font-black text-blue-400">{data.organizations.length}</p>
          <p className="text-[9px] text-slate-500 uppercase tracking-wider">UN bodies</p>
        </div>
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-3">
          <p className="text-lg font-black text-emerald-400">{data.diverse_team_pct}%</p>
          <p className="text-[9px] text-slate-500 uppercase tracking-wider">Diverse author teams</p>
        </div>
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-3">
          <p className="text-lg font-black text-white">{data.year_range[0]}–{data.year_range[1]}</p>
          <p className="text-[9px] text-slate-500 uppercase tracking-wider">Coverage</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-4">
        {data.organizations.map(o => (
          <span key={o.org} className="text-[9px] font-semibold text-blue-300/80 bg-blue-500/10 border border-blue-500/20 rounded-full px-2 py-0.5">
            {o.org}{o.count > 1 ? ` ×${o.count}` : ""}
          </span>
        ))}
      </div>

      {data.diversity_benchmark && (
        <div data-testid="diversity-benchmark" className="bg-white/[0.02] border border-white/[0.05] rounded-lg p-4 mb-4">
          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-[0.2em] mb-3">Diverse author teams — who builds the research?</p>
          <div className="space-y-2.5">
            {data.diversity_benchmark.map((b, i) => (
              <div key={b.group}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-semibold text-slate-300">{b.group}</span>
                  <span className="text-[10px] font-black" style={{ color: ["#10b981", "#3b82f6", "#f59e0b"][i] }}>
                    {b.diverse_pct}%<span className="text-slate-600 font-normal"> of {b.n.toLocaleString()}</span>
                  </span>
                </div>
                <div className="w-full h-2 bg-white/[0.05] rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-1000"
                    style={{ width: `${b.diverse_pct}%`, backgroundColor: ["#10b981", "#3b82f6", "#f59e0b"][i] }} />
                </div>
              </div>
            ))}
          </div>
          <p className="text-[9px] text-slate-500 mt-2">% of works with mixed or women-led author teams (papers with known composition).</p>
        </div>
      )}

      <div className="space-y-2">
        {shown.map(r => (
          <div key={r.paper_title} data-testid="un-report-row" className="bg-white/[0.02] border border-white/[0.05] rounded-lg p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs font-bold text-white leading-snug">
                  {r.source_url ? (
                    <a data-testid="un-report-link" href={r.source_url} target="_blank" rel="noopener noreferrer" className="hover:text-blue-300 transition-colors">
                      {r.paper_title} <ExternalLink className="w-2.5 h-2.5 inline-block opacity-60" />
                    </a>
                  ) : r.paper_title}
                </p>
                <p className="text-[10px] text-slate-500 mt-0.5">
                  <span className="text-blue-400/80 font-semibold">{r.venue}</span> · {r.year} · {(r.authors || []).slice(0, 3).join(", ")}{(r.authors || []).length > 3 ? ` +${r.authors.length - 3} more` : ""}
                </p>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <span className="text-[8px] font-bold uppercase tracking-wider rounded px-1.5 py-0.5"
                  style={{ color: ORIENT_COLORS[r.value_orientation] || "#94a3b8", backgroundColor: `${ORIENT_COLORS[r.value_orientation] || "#94a3b8"}1a` }}>
                  {r.value_orientation}
                </span>
                {(r.author_composition === "mixed" || r.author_composition === "female_only") && (
                  <span className="flex items-center gap-1 text-[8px] text-emerald-400/80"><Users className="w-2.5 h-2.5" />{r.author_composition === "mixed" ? "mixed team" : "women-led"}</span>
                )}
              </div>
            </div>
            {expanded && r.abstract && <p className="text-[10px] text-slate-400 mt-1.5 leading-relaxed">{r.abstract}</p>}
          </div>
        ))}
      </div>

      <button data-testid="un-reports-toggle" onClick={() => setExpanded(!expanded)}
        className="mt-3 flex items-center gap-1 text-[10px] font-semibold text-blue-400 hover:text-blue-300 transition-colors">
        {expanded ? <><ChevronUp className="w-3 h-3" /> Show less</> : <><ChevronDown className="w-3 h-3" /> Show all {data.total} reports</>}
      </button>
    </div>
  );
};
